import { ClickAwayListener, Grow, MenuItem, MenuList, Paper, Popper } from '@mui/material'
import React, { ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import NewButton from 'ui/atoms/NewButton/NewButton'
import styles from './ButtonDropdown.module.less'

type ButtonDropdownProps = {
  label: string
  variant?: 'primary' | 'outlined' | 'optimize' | 'success'
  icon?: string
  disabled?: boolean
  loading?: boolean
  tooltip?: {
    title: string
    placement?: 'top' | 'bottom' | 'left' | 'right'
  }
  children?: React.ReactNode
  maxWidth?: string
  dropdownItems?: { label: string; onClick: (event: React.MouseEvent<HTMLElement>) => void }[]
  'data-testid'?: string
  onButtonClick: (event: React.MouseEvent<HTMLElement>) => void
}

export default function ButtonDropdown({
  label,
  variant = 'primary',
  icon,
  disabled = false,
  loading = false,
  tooltip = {
    title: label ?? '',
    placement: 'top',
  },
  children,
  maxWidth,
  dropdownItems,
  onButtonClick,
  ...props
}: ButtonDropdownProps): ReactElement {
  const { t } = useTranslation()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)
  const showOutlinedDivider = variant === 'outlined'

  const handleDropdownClick = (event: React.MouseEvent<HTMLElement>): void => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = (): void => {
    setAnchorEl(null)
  }

  return (
    <>
      <div className={styles.ButtonDropdown_Container}>
        <NewButton
          variant={variant}
          label={label}
          icon={icon}
          iconSize="medium"
          disabled={disabled}
          loading={loading}
          tooltip={tooltip}
          maxWidth={maxWidth}
          onClick={onButtonClick}
          data-testid={props['data-testid']}
          style={{
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            borderRight: showOutlinedDivider ? undefined : 'none',
            paddingRight: '8px', // Reduced gap between buttons
            height: '38px',
          }}
        >
          {children}
        </NewButton>
        <NewButton
          variant={variant === 'optimize' ? 'primary' : variant}
          label=""
          icon="fal fa-chevron-down"
          iconSize="medium"
          disabled={disabled}
          onClick={handleDropdownClick}
          style={{
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            borderLeft: 'none',
            paddingLeft: '8px', // Reduced gap between buttons
            height: '38px',
          }}
          aria-expanded={open || undefined}
        />
      </div>
      <Popper open={open} anchorEl={anchorEl} transition style={{ zIndex: '10000' }}>
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList>
                  {dropdownItems?.map((item) => (
                    <MenuItem
                      key={item.label}
                      onClick={(event: React.MouseEvent<HTMLElement>) => {
                        item.onClick(event)
                        handleClose()
                      }}
                    >
                      {t(item.label)}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  )
}
